// Consent store — KVKK compliant, granular 4-category cookie preferences
// Stored in localStorage as 'ch_cookie_consent' JSON

const CONSENT_KEY = "ch_cookie_consent";
const CONSENT_VERSION = "3.0";
const CONSENT_EXPIRY_MS = 365 * 24 * 60 * 60 * 1000; // 12 months

export interface ConsentPreferences {
    necessary: true;
    analytics: boolean;
    marketing: boolean;
    personalizedContent: boolean;
    marketingNotifications: boolean;
    decidedAt: number;
    version: string;
}

const defaultConsent = (): ConsentPreferences => ({
    necessary: true,
    analytics: false,
    marketing: false,
    personalizedContent: false,
    marketingNotifications: false,
    decidedAt: 0,
    version: CONSENT_VERSION,
});

export const getConsent = (): ConsentPreferences => {
    try {
        const raw = localStorage.getItem(CONSENT_KEY);
        if (!raw) return defaultConsent();
        const stored = JSON.parse(raw) as Partial<ConsentPreferences> & { notifications?: boolean };
        const parsed: ConsentPreferences = {
            necessary: true,
            analytics: stored.analytics === true,
            marketing: stored.marketing === true,
            personalizedContent: stored.personalizedContent === true,
            marketingNotifications: stored.marketingNotifications === true,
            decidedAt: Number(stored.decidedAt || 0),
            version: stored.version || "",
        };
        // Expire after 12 months — re-ask annually
        if (Date.now() - parsed.decidedAt > CONSENT_EXPIRY_MS) {
            localStorage.removeItem(CONSENT_KEY);
            return defaultConsent();
        }
        if (parsed.version !== CONSENT_VERSION) return defaultConsent();
        return parsed;
    } catch {
        return defaultConsent();
    }
};

export const hasDecided = (): boolean => {
    try {
        const raw = localStorage.getItem(CONSENT_KEY);
        if (!raw) return false;
        const parsed: ConsentPreferences = JSON.parse(raw);
        return parsed.version === CONSENT_VERSION && parsed.decidedAt > 0 && Date.now() - parsed.decidedAt <= CONSENT_EXPIRY_MS;
    } catch {
        return false;
    }
};

export const setConsent = (prefs: Omit<ConsentPreferences, "necessary" | "decidedAt" | "version">): void => {
    const consent: ConsentPreferences = {
        necessary: true,
        analytics: prefs.analytics,
        marketing: prefs.marketing,
        personalizedContent: prefs.personalizedContent,
        marketingNotifications: prefs.marketingNotifications,
        decidedAt: Date.now(),
        version: CONSENT_VERSION,
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    if (!consent.analytics) localStorage.removeItem("ch_first_touch_attribution");
    // Dispatch event so services can react immediately without page reload
    window.dispatchEvent(new CustomEvent("ch-consent-updated", { detail: consent }));
};

export const acceptAll = (): void => {
    setConsent({ analytics: true, marketing: true, personalizedContent: true, marketingNotifications: true });
};

export const rejectAll = (): void => {
    setConsent({ analytics: false, marketing: false, personalizedContent: false, marketingNotifications: false });
};
